/**
 * `live-shaped` profile rules — the numbers, and nothing else (plan §1.2 · §4.7).
 *
 * Split out of `adapters/local/live-shaped.ts` so this module imports NOTHING.
 * Two consumers need these constants and only one of them can resolve a
 * package graph: `scripts/extract-demo-fixtures-lib.ts` runs under
 * `node --experimental-strip-types`, which does not resolve the extensionless
 * relative specifiers `@meridian/contracts`' barrel is written with. Importing
 * the adapter — whose first statement is a value import of `contracts` —
 * therefore broke `pnpm fixtures:extract` with ERR_MODULE_NOT_FOUND, and no
 * gate caught it: tsc resolves extensionless fine, ESLint disables the rule
 * under `scripts/**`, dependency-cruiser's `includeOnly` excludes `scripts/`,
 * and no workflow invokes the script.
 *
 * Keeping the rules here rather than duplicating them is the point: the
 * runtime adapter and the committed fixtures must not be able to describe two
 * different "live-shaped". `adapters/local/live-shaped.ts` re-exports every
 * symbol below, so existing importers are unaffected.
 */

/** Closed profile vocabulary; `apps/web` validates `VITE_SYNTHETIC_PROFILE` against it. */
export const SYNTHETIC_PROFILES = Object.freeze(['demo-fidelity', 'live-shaped'] as const);
export type SyntheticProfile = (typeof SYNTHETIC_PROFILES)[number];

/**
 * Every 17th row loses its P/E, every 23rd its dividend, and row 0 stands in for
 * a recent listing with no usable history — PLUS any row whose demo value is
 * already 0, which both consumers apply with `||` (see the note beside that
 * disjunction in `adapters/local/live-shaped.ts`). Coprime moduli so the two
 * null sets overlap on exactly one row in 391 rather than on every shared
 * multiple.
 */
export const LIVE_SHAPE_NULL_PE_MODULUS = 17;
export const LIVE_SHAPE_NULL_DIV_MODULUS = 23;
export const LIVE_SHAPE_INSUFFICIENT_HISTORY_INDEX = 0;

export interface OutOfClampSample {
  readonly a: string;
  readonly b: string;
  readonly rho: number;
}

/**
 * Pairs whose ρ is forced outside the synthetic [-0.85, 0.95] clamp band.
 *
 * Named pairs rather than positional ones, so the value a reader sees in the
 * committed fixtures is the value the runtime adapter emits. A pair whose
 * tickers are absent from the row set is skipped: an injected fixture of one
 * sector is a legitimate row set, and throwing there would make the profile
 * unusable for exactly the small sets tests are built from.
 */
export const OUT_OF_CLAMP_SAMPLES: readonly OutOfClampSample[] = Object.freeze([
  Object.freeze({ a: 'AAPL', b: 'MSFT', rho: 0.98 }),
  Object.freeze({ a: 'XOM', b: 'TSLA', rho: -0.91 }),
]);
